import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import Magnetic from './Magnetic';

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About Us' },
  { path: '/vision', label: 'Vision & Mission' },
];

const serviceLinks = [
  { path: '/services/digital-services', label: 'Digital Services' },
  { path: '/services/printing', label: 'Printing' },
  { path: '/services/vehicle-wraps', label: 'Vehicle Wraps & Signage' },
  { path: '/services/branding', label: 'Branding & Identity' },
  { path: '/services/corporate-stationery', label: 'Corporate Stationery & Gifts' },
  { path: '/services/apparel-printing', label: 'Apparel Printing' },
  { path: '/services/company-registration', label: 'Company Registration & Compliance' },
];

const trailingNavItems = [
  { path: '/our-work', label: 'Our Work' },
  { path: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const servicesRef = useRef<HTMLDivElement>(null);

  // Transparent over the hero, frosted-white once you scroll past ~40px.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Re-evaluate on route change (ScrollToTop resets scroll to 0).
  useEffect(() => {
    setScrolled(window.scrollY > 40);
    setIsOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  // Close the desktop Services dropdown on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const solid = scrolled;
  const onServicesRoute = location.pathname.startsWith('/services');

  return (
    <>
    <nav
      className="fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-400"
      style={
        solid
          ? {
              backgroundColor: 'rgba(246,248,252,0.92)',
              backdropFilter: 'blur(18px) saturate(1.3)',
              WebkitBackdropFilter: 'blur(18px) saturate(1.3)',
              borderBottom: '1px solid rgba(10,17,34,0.08)',
              boxShadow: '0 12px 32px -20px rgba(10,17,34,0.35)',
            }
          : {
              backgroundColor: 'transparent',
              borderBottom: '1px solid transparent',
            }
      }
    >
      {/* Top scrim: guarantees logo/link legibility over bright hero imagery while the
          header is transparent; fades out and is removed once the bar turns solid white. */}
      {!solid && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-28"
          style={{ background: 'linear-gradient(180deg, rgba(5,8,15,0.72) 0%, rgba(5,8,15,0) 100%)' }}
        />
      )}
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-4 py-4 sm:px-10">
        <Link to="/" aria-label="DKSN Business Solutions — home" className="inline-flex">
          <img
            src="/images/logo-small.webp?v=2"
            alt="DKSN Business Solutions"
            width={260}
            height={107}
            className="h-8 md:h-12 w-auto"
            style={{
              filter: solid ? 'none' : 'brightness(0) invert(1)',
              transition: 'filter 0.4s ease',
            }}
          />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            const color = solid
              ? active
                ? 'text-primary-900'
                : 'text-[#44536E] hover:text-primary-900'
              : active
              ? 'text-white'
              : 'text-[#A7B4CC] hover:text-white';
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors duration-300 hover:bg-[rgba(128,148,180,0.14)] ${color}`}
              >
                {item.label}
              </Link>
            );
          })}

          {/* Services dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              className={`flex items-center gap-1 rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors duration-300 hover:bg-[rgba(128,148,180,0.14)] ${
                solid
                  ? onServicesRoute
                    ? 'text-primary-900'
                    : 'text-[#44536E] hover:text-primary-900'
                  : onServicesRoute
                  ? 'text-white'
                  : 'text-[#A7B4CC] hover:text-white'
              }`}
            >
              Services
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <div
              className={`absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 pt-3 transition-all duration-200 ${
                servicesOpen ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none -translate-y-1 opacity-0'
              }`}
            >
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0B1322] p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]">
                <Link
                  to="/services"
                  onClick={() => setServicesOpen(false)}
                  className={`block rounded-xl px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors ${
                    location.pathname === '/services'
                      ? 'bg-white/10 text-white'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  All Services
                </Link>
                <div className="my-1 border-t border-white/10" />
                {serviceLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setServicesOpen(false)}
                    className={`block rounded-xl px-3.5 py-2.5 text-[13.5px] transition-colors ${
                      location.pathname === link.path
                        ? 'bg-white/10 text-white'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {trailingNavItems.map((item) => {
            const active = location.pathname === item.path;
            const color = solid
              ? active
                ? 'text-primary-900'
                : 'text-[#44536E] hover:text-primary-900'
              : active
              ? 'text-white'
              : 'text-[#A7B4CC] hover:text-white';
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors duration-300 hover:bg-[rgba(128,148,180,0.14)] ${color}`}
              >
                {item.label}
              </Link>
            );
          })}

          <Magnetic>
            <Link
              to="/quote"
              className="ml-2 inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300"
              style={
                solid
                  ? {
                      backgroundColor: '#0E1830',
                      color: '#fff',
                      boxShadow: '0 8px 26px -10px rgba(14,24,48,0.55)',
                    }
                  : {
                      backgroundColor: '#EEF2FA',
                      color: '#05080F',
                      boxShadow: '0 8px 26px -10px rgba(255,255,255,0.4)',
                    }
              }
            >
              Start a Project
            </Link>
          </Magnetic>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-2 -mr-2 transition-colors md:hidden ${
            solid ? 'text-primary-900' : 'text-white'
          }`}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
    </nav>

      {/* Mobile Drawer */}
      <div
        aria-hidden={!isOpen}
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setIsOpen(false)}
      />
      <div
        className={`fixed inset-y-0 right-0 z-50 flex h-full w-[86%] max-w-sm transform flex-col overflow-y-auto bg-primary-950 shadow-[-24px_0_60px_-20px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <Link to="/" onClick={() => setIsOpen(false)} className="inline-flex">
            <img src="/images/logo-small.webp?v=2" alt="DKSN Business Solutions" width={260} height={107} className="h-8 w-auto" />
          </Link>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 -mr-2 text-white"
            aria-label="Close menu"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 space-y-1 px-4 py-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${
                location.pathname === item.path
                  ? 'bg-white/5 text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              } block rounded-lg px-4 py-3 text-base font-medium transition-colors duration-200`}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))}

          {/* Services accordion */}
          <div>
            <button
              type="button"
              onClick={() => setMobileServicesOpen((v) => !v)}
              aria-expanded={mobileServicesOpen}
              className={`${
                onServicesRoute ? 'bg-white/5 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
              } flex w-full items-center justify-between rounded-lg px-4 py-3 text-base font-medium transition-colors duration-200`}
            >
              Services
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                mobileServicesOpen ? 'max-h-[34rem] overflow-y-auto opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="ml-2 mt-1 space-y-1 border-l border-white/10 pl-3">
                <Link
                  to="/services"
                  onClick={() => setIsOpen(false)}
                  className={`${
                    location.pathname === '/services'
                      ? 'bg-white/5 text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                  } block rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors duration-200`}
                >
                  All Services
                </Link>
                {serviceLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`${
                      location.pathname === link.path
                        ? 'bg-white/5 text-white'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    } block rounded-lg px-3 py-2.5 text-sm transition-colors duration-200`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {trailingNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${
                location.pathname === item.path
                  ? 'bg-white/5 text-white'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              } block rounded-lg px-4 py-3 text-base font-medium transition-colors duration-200`}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="border-t border-white/10 px-4 py-4">
          <Link
            to="/quote"
            onClick={() => setIsOpen(false)}
            className="block rounded-full bg-gray-100 px-5 py-3 text-center text-base font-semibold text-primary-950"
          >
            Start a Project
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;
